import { Pinia, Store } from 'pinia-class-component';
import { CellClient, Cell, CellList } from '../components/backend/backendapi';
import { initClient } from '../api';
import { mapTree } from '../components/cell/cell.util';
import { CellTree } from '../components/cell/cell.model';

@Store({
  id: 'CellStore',
  name: 'CellStore',
})
export default class CellStore extends Pinia {
  private readonly cellApi = initClient(CellClient);

  //data
  private _loadingPromise: Promise<CellList[]> | null = null;
  private _cells: CellList[] = [];

  //getter
  get loading(): boolean {
    return this._loadingPromise !== null;
  }

  get cells(): CellList[] {
    return this._cells;
  }

  get cellsById(): Map<number, CellList> {
    return new Map(this._cells.map(c => [c.id, c]));
  }

  get cellTree(): CellTree[] {
    return mapTree(this.cells);
  }

  get cellsForVSelect(): { id: number, name: string }[] {
    let flatMapper = (tree: CellTree, prefix: string) => {
      return [{ id: tree.item.id!, name: prefix + tree.item.name!, ident: tree.item.ident }]
        .concat(tree.children.map(e => flatMapper(e, prefix + tree.item.name! + ' > ')).reduce((l, r) => l.concat(r), []));
    };
    return this.cellTree.map(e => flatMapper(e, '')).reduce((l, r) => l.concat(r), []);
  }

  //actions
  clear(): void {
    this._cells.splice(0);
  }

  setCells(cells: CellList[]): void {
    this._cells.splice(0);
    this._cells.push(...cells);
  }

  addCell(cell: CellList): void {
    this._cells.push(cell);
  }

  updateCell(cell: CellList): void {
    const index = this._cells.findIndex(e => e.id == cell.id);
    if (index >= 0) {
      this._cells.splice(index, 1, cell);
    } else {
      this._cells.push(cell);
    }
  }

  forgetCell(cell: CellList): void {
    const index = this._cells.findIndex(e => e.id == cell.id);
    if (index >= 0) {
      this._cells.splice(index, 1);
    }
  }

  async reload(force: boolean = false): Promise<void> {
    if (this._loadingPromise) {
      await this._loadingPromise;
      if (!force) {// when force is true, wait for the current operation to complete, then reload
        return;
      }
    }

    try {
      this._loadingPromise = this.cellApi.getCellsAndTreeData();
      const cells = await this._loadingPromise;
      this.setCells(cells);
    } catch (err) {
      this.clear();
    } finally {
      this._loadingPromise = null;
    }
  }

  async loadIfAbsent(): Promise<void> {
    if (this._cells.length > 0) {
      return;
    }
    return await this.reload();
  }
}